import type { Attachment } from 'api/models/mainservice';
import type { CustomDataObject, GeoCoordinate, OrderPriority, TableQuery } from 'common/models';
import type { InterventionState } from 'pages/interventions/models';
import type { OwnedServiceObject, ServiceObjectInCustomer } from 'pages/serviceObjects/models';

import type { OrderCreationSource, OrderState } from '../orders/models';

export interface Customer {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  website?: string | null;
  description?: string | null;
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  kind: CustomerKind;
  /** @format uuid */
  typeId: string;
  typeName: string;
  /** @format uuid */
  ownerId: string;
  ownerName: string;
  /** @format uuid */
  addressId?: string | null;
  address?: Address | null;
  /** @format uuid */
  contactAddressId?: string | null;
  contactAddress?: Address | null;
  /** @format uuid */
  primaryContactPersonId?: string | null;
  primaryContactPerson?: ContactPerson | null;
  /** @format uuid */
  priceListId?: string | null;
  priceListName?: string | null;
  customData?: CustomDataObject;
  externalId?: string | null;
  sendByPost: boolean;
}

export interface CustomerDetail {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  website?: string | null;
  description?: string | null;
  reportEmails: string[];
  nextRevisionEmails: string[];
  notificationEmails: string[];
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  /** @format uuid */
  typeId: string;
  typeName: string;
  /** @format uuid */
  invoiceAddressId?: string | null;
  /** @format uuid */
  contactAddressId?: string | null;
  /** @format uuid */
  primaryContactPersonId?: string | null;
  primaryContactPerson?: ContactPerson | null;
  kind: CustomerKind;
  /** @format uuid */
  ownerId: string;
  ownerName: string;
  /** @format uuid */
  priceListId?: string | null;
  priceListName?: string | null;
  orders: OrderInCustomer[];
  addresses: Address[];
  serviceObjects: ServiceObjectInCustomer[];
  ownedServiceObjects: OwnedServiceObject[];
  contactPeople: ContactPerson[];
  workItems: CustomerWorkItem[];
  orderCategoryReportEmails: CustomerOrderCategoryReportEmails[];
  attachments: Attachment[];
  customData?: CustomDataObject;
  externalId?: string | null;
  sendByPost: boolean;
}

export interface OrderInCustomer {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  externalId?: string | null;
  code: string;
  name?: string | null;
  address: Address;
  /** @format uuid */
  contactPersonId: string;
  contactPerson: ContactPerson;
  /** @format uuid */
  categoryId: string;
  priority: OrderPriority;
  state: OrderState;
  waitingForCustomerInfo: boolean;
  customerFilledInfo: boolean;
  hasInterventions: boolean;
  creationSource: OrderCreationSource;
  /** @format date-time */
  completedAt?: string | null;
  /** @format date-time */
  cancelledAt?: string | null;
  cancelReason?: string | null;
  /** @format uuid */
  cancelledById?: string | null;
  interventions: InterventionInCustomer[];
  serviceObjects: ServiceObjectInCustomer[];
}

export interface InterventionInCustomer {
  /** @format uuid */
  categoryId: string;
  categoryName: string;

  /** @format uuid */
  primaryAssigneeId: string;

  /** @format date-time */
  day: string;
  /** @format date-time */
  departureStartedAt?: string | null;

  /** @format date-time */
  startedAt?: string | null;

  /** @format date-time */
  endedAt?: string | null;

  /** @format date-time */
  confirmedAt?: string | null;

  /** @format date-time */
  cancelledAt?: string | null;
  state: InterventionState;

  /** @format uuid */
  id: string;

  /** @format date-time */
  created: string;

  /** @format date-time */
  modified: string;
}

export interface AresCustomerInfo {
  name: string;
  identificationNumber: string;
  address: string;
}

export interface AresCustomerDetail {
  name: string;
  identificationNumber: string;
  taxNumber?: string | null;
  street: string;
  city: string;
  postalCode: string;
}

export interface ContactPerson {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  /** @format uuid */
  customerId: string;
  firstName: string;
  lastName: string;
  position?: string | null;
  phone?: PhoneNumber | null;
  email?: string | null;
  description?: string | null;
  sendNotifications: boolean;
  sendReports: boolean;
  /** @format uuid */
  addressId?: string | null;
  externalId?: string | null;
  /** @format uuid */
  customerUserId?: string | null;
  /** @format date-time */
  customerUserInvitationSent?: string | null;
  /** @format date-time */
  customerUserInvitationAccepted?: string | null;
  /** @format uuid */
  customerUserInvitationGuid?: string | null;
  customerUserIsEditor: boolean;
  customerUserIsDisabled: boolean;
  customerUserEmail?: string | null;
}

export interface PhoneNumber {
  prefix: string;
  number: string;
}

export interface Address {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name?: string | null;
  street: string;
  city: string;
  country?: string | null;
  postalCode: string;
  description?: string | null;
  hasFlatRateTravelFee: boolean;
  /** @format double */
  travelFee?: number | null;
  /** @format double */
  pricePerKilometer?: number | null;
  coordinates: GeoCoordinate;
  externalId?: string | null;
}

export interface CustomerToAdd {
  /** @format uuid */
  id?: string | null;
  externalId?: string | null;
  name: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  website?: string | null;
  description?: string | null;
  /** @format uuid */
  categoryId: string;
  /** @format uuid */
  typeId: string;
  kind?: CustomerKind;
  /** @format uuid */
  ownerId?: string | null;
  customData?: string | null;
  sendByPost?: boolean;
  /** @format uuid */
  linkedOrderRequestId?: string | null;
  /** ContactPersonToAdd */
  contactPerson: ContactPersonToAdd;
  /** CustomerAddressToAdd */
  invoiceAddress: AddressToAdd;
  /**
   * CustomerAddressToAdd
   * CustomerAddressToAdd
   */
  contactAddress?: AddressToAdd | null;
  attachments?: string[];
  /** @format uuid */
  priceListId?: string | null;
}

export interface AddCustomerRequest {
  id?: string | null;
  name: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  website?: string | null;
  description?: string | null;
  categoryId: string;
  typeId: string;
  kind: CustomerKind;
  contactPerson: ContactPersonToAdd;
  invoiceAddress: AddressToAdd;
  contactAddress?: AddressToAdd;
  customData?: string | null;
  externalId?: string | null;
}

export interface ContactPersonToAdd {
  /** @format uuid */
  id?: string;
  externalId?: string | null;
  firstName: string;
  lastName: string;
  position?: string | null;
  phone?: PhoneNumber | null;
  email?: string | null;
  description?: string | null;
  sendNotifications?: boolean;
  sendReports?: boolean;
  /** @format uuid */
  addressId?: string | null;
}

export interface ContactPersonToEdit {
  firstName?: string | null;
  lastName?: string | null;
  position?: string | null;
  phone?: PhoneNumber | null;
  email?: string | null;
  description?: string | null;
  sendNotifications?: boolean;
  sendReports?: boolean;
  externalId?: string | null;
  /** @format uuid */
  addressId?: string | null;
}

export interface AddressToAdd {
  /** @format uuid */
  id?: string;
  externalId?: string | null;
  name?: string | null;
  street: string;
  city: string;
  postalCode: string;
  country?: string | null;
  /** @format double */
  latitude: number;
  /** @format double */
  longitude: number;
  hasFlatRateTravelFee?: boolean;
  /** @format double */
  travelFee?: number | null;
  /** @format double */
  pricePerKilometer?: number | null;
  description?: string | null;
}

export interface AddressToEdit {
  street: string;
  city: string;
  postalCode: string;
  country?: string | null;
  /** @format double */
  latitude: number;
  /** @format double */
  longitude: number;
  hasFlatRateTravelFee: boolean;
  /** @format double */
  travelFee?: number | null;
  /** @format double */
  pricePerKilometer?: number | null;
  description?: string | null;
  externalId?: string | null;
}
export type CustomerKind = 'Household' | 'Company';

export const CustomerKindArray: CustomerKind[] = ['Household', 'Company'];

export const CUSTOMER_SORT_KEYS = [
  'name',
  'address',
  'categoryName',
  'typeName',
  'identificationNumber',
  'ownerName',
  'taxNumber',
  'kind',
  'primaryContactPerson',
  'externalId',
  'sendByPost',
  'description',
  'contactAddress',
  'priceListName',
  'created'
] as const;

export type CustomerSortBy = (typeof CUSTOMER_SORT_KEYS)[number];

export interface CustomerTableQuery extends TableQuery<CustomerSortBy> {
  categoryId?: string | null;
  customerTypeId?: string | null;
  searchPhrase?: string | null;
  ownerId?: string | null;
  priceListId?: string | null;
}

export interface CustomerWorkItem {
  /** @format uuid */
  workItemId: string;
  /** @format double */
  price?: number | null;
  isHidden: boolean;
}

export interface CustomerWorkItemToEdit {
  /** @format uuid */
  workItemId: string;
  /** @format double */
  price?: number | null;
  isHidden: boolean;
}

export interface CustomerOrderCategoryReportEmails {
  orderCategoryId: string;
  reportEmails?: string[];
}

export interface CustomerReportEmailsToEdit {
  categoryId?: string | null;
  reportEmails?: string[];
}

export interface ExistingCustomer {
  /** @format uuid */
  id: string;
  name: string;
  kind: CustomerKind;
  identificationNumber?: string | null;
  taxNumber?: string | null;
}

export interface ExistingCustomerQuery {
  excludeCustomerId?: string | null;
  name?: string | null;
  identificationNumber?: string | null;
  taxNumber?: string | null;
}

export interface ExistingContactPerson {
  customer: ExistingCustomer;
  firstName: string;
  lastName: string;
  phone?: PhoneNumber | null;
  email?: string | null;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
}

export interface ExistingContactPersonQuery {
  customerId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  excludeContactPersonId?: string | null;
}

export interface ExistingCustomerAddressQuery {
  street?: string | null;
  city?: string | null;
  excludeAddressId?: string | null;
}

export interface CustomerNotificationEmailsToEdit {
  emails: string[];
}

export interface ContactPersonToInvite {
  isEditor: boolean;
}
